/**
 * Parent-scan landing & action page.
 * Opens when a school guard/staff scans the parent QR on a student card.
 * Shows student info + guardian info and logs entry/exit/pickup events that
 * notify all guardians via SMS (through the backend).
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, LogIn, LogOut, UserCheck, AlertCircle, Phone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ParentScanPage() {
  const { token } = useParams();
  const [note, setNote] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['parent-scan-landing', token],
    queryFn: async () => {
      const res = await fetch(`${API}/api/public/parent-scan/${token}`);
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Invalid code');
      return d;
    },
    retry: false,
  });

  const logMut = useMutation({
    mutationFn: async (action) => {
      const res = await fetch(`${API}/api/public/parent-scan/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, action, zone: 'main_gate', note }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');
      return d;
    },
    onSuccess: (r) => {
      setLastResult(r);
      toast.success(`Logged ${r.action.toUpperCase()} · ${r.guardians_notified} parent${r.guardians_notified !== 1 ? 's' : ''} notified`);
      setNote('');
    },
    onError: e => toast.error(e.message),
  });

  if (isLoading) {
    return <div className="min-h-screen bg-slate-900 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>;
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="parent-scan-error">
        <div className="bg-red-900/40 border border-red-700 rounded-xl p-6 max-w-md text-center text-white">
          <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-2" />
          <p className="text-lg font-semibold">Invalid parent code</p>
          <p className="text-sm text-red-200 mt-1">{error?.message || 'This code is not valid. Please use a freshly-printed student card.'}</p>
        </div>
      </div>
    );
  }

  const s = data.student || {};
  const inst = data.institution || {};
  return (
    <div className="min-h-screen p-4 flex items-center justify-center" style={{ background: `linear-gradient(160deg, ${inst.primary_color || '#1e3a5f'} 0%, #0f172a 80%)` }} data-testid="parent-scan-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="p-5 text-white" style={{ background: `linear-gradient(135deg, ${inst.primary_color}, ${inst.primary_color}dd)` }}>
            <div className="flex items-center gap-3">
              {inst.logo_url && <img src={inst.logo_url} alt="" className="h-10 w-10 rounded-lg bg-white/15 object-contain p-1" />}
              <div>
                <p className="text-[10px] uppercase tracking-widest opacity-80">Parent Check-in</p>
                <p className="font-bold text-sm">{inst.name}</p>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 text-xl font-bold">
                {s.photo_url ? <img src={s.photo_url} alt="" className="h-full w-full object-cover rounded-full" /> : (s.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div>
                <p className="text-lg font-bold text-slate-900" data-testid="parent-scan-student-name">{s.name}</p>
                <p className="text-xs text-slate-500">ID: <span className="font-mono">{s.admission_number}</span></p>
                {s.class && <p className="text-xs text-slate-500">Class: {s.class}</p>}
              </div>
            </div>

            {(data.guardians || []).length > 0 ? (
              <div className="border-t pt-3">
                <p className="text-[10px] uppercase text-slate-400 mb-1">Notifies these guardians:</p>
                {data.guardians.map(g => (
                  <div key={g.id} className="flex justify-between items-center text-xs py-1">
                    <div>
                      <p className="font-medium">{g.name}</p>
                      <p className="text-[10px] text-slate-400 capitalize">{g.relation}</p>
                    </div>
                    <span className="text-slate-500 flex items-center gap-1"><Phone className="h-3 w-3" />…{g.phone_last4 || '----'}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border-t pt-3 text-[11px] text-amber-600 bg-amber-50 p-2 rounded">
                No guardian on record — event will be logged but no notification sent.
              </div>
            )}

            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} placeholder="Reason, pickup by name, etc." data-testid="parent-scan-note" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <Button className="bg-emerald-500 hover:bg-emerald-600" onClick={() => logMut.mutate('entry')} disabled={logMut.isPending} data-testid="parent-scan-entry">
                <LogIn className="h-4 w-4 mr-1" />Entry
              </Button>
              <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => logMut.mutate('exit')} disabled={logMut.isPending} data-testid="parent-scan-exit">
                <LogOut className="h-4 w-4 mr-1" />Exit
              </Button>
              <Button className="bg-indigo-500 hover:bg-indigo-600" onClick={() => logMut.mutate('pickup')} disabled={logMut.isPending} data-testid="parent-scan-pickup">
                <UserCheck className="h-4 w-4 mr-1" />Pickup
              </Button>
            </div>

            {lastResult && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs text-emerald-800" data-testid="parent-scan-result">
                ✅ {lastResult.action.toUpperCase()} logged · {lastResult.guardians_notified} guardian{lastResult.guardians_notified !== 1 ? 's' : ''} notified.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
